import { get } from '../../lib/api/client';
import { fromP1, toP1Query, type P1Response, type Page, type PageRequest } from '../../lib/api/paging';
import type { AddressShape, PersonRef } from '../dishes/api';

/**
 * Orders — `GET /admin/orders/{id}/detail` (api-core-go
 * `admin-order-listing.go`) and the traceability search
 * `GET /admin/orders/search` (P1, needs one of byOrderId / byDishId /
 * byListingId / byChefId).
 */

/** `model.Order`. Money in cents. */
export interface Order {
  id: string;
  createdAt: string;
  updatedAt: string;
  orderNumber: string;
  status: string;
  orderedFor: string;
  listingId: string;
  quantity: number;
  fulfillmentMethod: string;
  saleDate?: string;
  intendedPickupTime?: string;
  pickupReminderSentAt?: string;
  readyPromptSentAt?: string;
  lateReminderStage: number;
  completedAt?: string;
  pricePerUnit: number;
  subtotal: number;
  totalPaid: number;
  heldUntil?: string;
  stripePaymentId?: string;
  rejectionReason?: string;
  verificationCode: number;
  ordererConfirmedAt?: string;
  listerConfirmedAt?: string;
  onMyWayAt?: string;
  listerNudgedAt?: string;
  orderAddress?: AddressShape;
}

/**
 * `model.TransactionSnapshot` — written at capture, values in force at
 * tax-calc time. Money in cents; `effectiveTaxRateAtCalc` and
 * `bufferFeePercentageAtCalc` are FRACTIONS (0.035 = 3.5%).
 */
export interface TransactionSnapshot {
  id: string;
  createdAt: string;
  orderId: string;
  profileId: string;
  chefProfileId: string;
  localeIdAtCalc: string;
  cityIdAtCalc?: string;
  fulfillmentType: string;
  transactionType: string;
  stripeTaxCalcSizingCalculationId: string;
  stripeTaxCalcCollectedCalculationId: string;
  taxableBaseCents: number;
  effectiveTaxRateAtCalc: number;
  targetQuarterCents: number;
  activeRegistrationIds: string[] | null;
  bufferFeePercentageAtCalc: number;
  bufferFeeSource: string;
  foodTaxCodeAtCalc: string;
  roundUpIncrementAtCalc: number;
  minRevenueAtCalc: number;
  feeInTaxBaseModeAtCalc: string;
  fromAddressStreet: string;
  fromAddressCity: string;
  fromAddressStateProvince: string;
  fromAddressPostalCode: string;
  toAddressStreet: string;
  toAddressCity: string;
  toAddressStateProvince: string;
  toAddressPostalCode: string;
  grossCents: number;
  taxCents: number;
  stripeFeeCents: number;
  bufferCents: number;
  rdfCents: number;
  /** Includes the absorbed penny drift. */
  revenueCents: number;
  pennyDriftCents: number;
  chargeTotalCents: number;
  originalAuthorized?: Record<string, unknown>;
  cancellationBreakdown?: Record<string, unknown>;
  chefLiabilityRecognizedCents: number;
  paymentMethod: string;
  stripeSettlementId?: string;
  stripeSettlementDate?: string;
  stripeReportedProcessingFeeCents?: number;
  stripePayoutId?: string;
  listerBalanceId?: string;
}

export interface OrderDetail {
  order: Order;
  eater: PersonRef | null;
  chef: PersonRef | null;
  /** null before capture, and for cancellation-fee-only captures. */
  snapshot: TransactionSnapshot | null;
}

export async function getOrderDetail(id: string, signal?: AbortSignal): Promise<OrderDetail> {
  return get<OrderDetail>(`/admin/orders/${encodeURIComponent(id)}/detail`, undefined, signal);
}

export interface OrderSearchRow {
  orderId: string;
  orderNumber: string;
  status: string;
  quantity: number;
  orderedAt: string;
  listingId: string;
  dishTitle: string;
  chefProfileId: string;
  eaterProfileId: string;
  eaterDisplayName: string;
  chefDisplayName: string;
  totalPaid: number;
}

export interface OrderSearchParams extends PageRequest {
  byOrderId?: string;
  byDishId?: string;
  byListingId?: string;
  byChefId?: string;
  /** Case-insensitive substring of the listing title. */
  byDishName?: string;
  /** Case-insensitive substring of the chef's display name. */
  byChefName?: string;
  /** Narrows any lookup to listings in these locales. */
  localeIds?: string[];
}

export async function searchOrders(
  { page, pageSize, ...by }: OrderSearchParams,
  signal?: AbortSignal,
): Promise<Page<OrderSearchRow>> {
  const res = await get<P1Response<OrderSearchRow>>(
    '/admin/orders/search',
    { ...by, ...toP1Query({ page, pageSize }) },
    signal,
  );
  return fromP1(res);
}
